// Theme settings control: light/dark toggle + per-theme color customization.
// The theme choice is persisted in localStorage PER USER and applied via
// `data-theme="light"|"dark"` on <html> (the CSS variable sets in
// index.css / App.css use it to re-tint the whole app). Storage keys are
// namespaced by the signed-in user's id (`bandz-theme:<userId>`, ...) so two
// people on the same browser keep separate themes; when signed out they share
// the `:anon` namespace. Legacy global keys are migrated on first load.
//
// Color customization is stored PER THEME (light and dark each hold their own
// { accent, bg, text } set), persisted as JSON in `bandz-theme-settings:<userId>`,
// and applied by overriding the CSS variables on <html> via
// `document.documentElement.style.setProperty(...)`. It layers on top of the
// active Roadcase theme: a `null` value means "use the Roadcase default".

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const THEME_STORAGE_KEY = 'bandz-theme';
const SETTINGS_STORAGE_KEY = 'bandz-theme-settings';
// Legacy single-accent key, kept only to migrate old users into the per-theme model.
const LEGACY_ACCENT_STORAGE_KEY = 'bandz-accent';
// Each user gets their OWN set of theme keys, namespaced by userId (e.g.
// `bandz-theme:7`). Two people on the same browser therefore keep separate
// themes. When no user is signed in we use a shared `:anon` namespace.
const ANON_KEY = 'anon';

/** Namespace a storage key by the signed-in user id (fallback: `:anon`). */
function scoped(base: string, userId: number | null): string {
  return `${base}:${userId ?? ANON_KEY}`;
}

/**
 * Read a namespaced key, falling back to the legacy GLOBAL key and migrating
 * it into this user's namespace on first load. Returns null when neither
 * exists (or storage is unavailable).
 */
function readMigrated(base: string, userId: number | null): string | null {
  try {
    const perUser = localStorage.getItem(scoped(base, userId));
    if (perUser != null) return perUser;
  } catch {
    /* ignore */
  }
  let legacy: string | null = null;
  try {
    legacy = localStorage.getItem(base);
  } catch {
    /* ignore */
  }
  if (legacy != null && userId != null) {
    // One-time migration: move the shared value into this user's namespace.
    try {
      localStorage.setItem(scoped(base, userId), legacy);
      localStorage.removeItem(base);
    } catch {
      /* ignore */
    }
  }
  return legacy;
}

type ThemeMode = 'light' | 'dark';
type ColorValue = string | null;

interface ThemeSettings {
  accent: ColorValue;
  bg: ColorValue;
  text: ColorValue;
}

type SettingsByTheme = Record<ThemeMode, ThemeSettings>;

const DEFAULT_SETTINGS: SettingsByTheme = {
  dark: { accent: null, bg: null, text: null },
  light: { accent: null, bg: null, text: null },
};

interface ColorPreset {
  name: string;
  value: string;
}

// Quick-select accent swatches. The first (Amber) is the Roadcase default.
const ACCENT_PRESETS: ColorPreset[] = [
  { name: 'Amber', value: '#e0a44c' },
  { name: 'Brass', value: '#c08a3e' },
  { name: 'Copper', value: '#c97b4a' },
  { name: 'Sage', value: '#84b06a' },
  { name: 'Steel', value: '#7fa8c9' },
  { name: 'Clay', value: '#d46a52' },
];

// Background swatches, one set per theme so the offered neutrals fit the mode.
const BACKGROUND_PRESETS: Record<ThemeMode, ColorPreset[]> = {
  dark: [
    { name: 'Default', value: '#141310' },
    { name: 'Elevated', value: '#1b1a16' },
    { name: 'Surface', value: '#211f1a' },
    { name: 'Deep', value: '#0f0e0c' },
    { name: 'Graphite', value: '#26241d' },
  ],
  light: [
    { name: 'Default', value: '#f4f1ea' },
    { name: 'White', value: '#ffffff' },
    { name: 'Paper', value: '#faf7f0' },
    { name: 'Warm', value: '#f1ede4' },
    { name: 'Stone', value: '#ebe7dd' },
  ],
};

// Text swatches, one set per theme so the offered colors contrast on the bg.
const TEXT_PRESETS: Record<ThemeMode, ColorPreset[]> = {
  dark: [
    { name: 'Default', value: '#eae7dd' },
    { name: 'White', value: '#ffffff' },
    { name: 'Soft', value: '#a7a193' },
    { name: 'Ivory', value: '#f2efe6' },
  ],
  light: [
    { name: 'Default', value: '#26231d' },
    { name: 'Black', value: '#000000' },
    { name: 'Slate', value: '#5c564b' },
    { name: 'Ink', value: '#1a1814' },
  ],
};

function getInitialTheme(userId: number | null): ThemeMode {
  const saved = readMigrated(THEME_STORAGE_KEY, userId);
  if (saved === 'light' || saved === 'dark') return saved;
  // Fall back to the OS preference.
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

function getInitialSettings(userId: number | null): SettingsByTheme {
  const raw = readMigrated(SETTINGS_STORAGE_KEY, userId);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<SettingsByTheme>;
      if (parsed && typeof parsed === 'object') {
        return {
          dark: {
            accent: parsed.dark?.accent ?? null,
            bg: parsed.dark?.bg ?? null,
            text: parsed.dark?.text ?? null,
          },
          light: {
            accent: parsed.light?.accent ?? null,
            bg: parsed.light?.bg ?? null,
            text: parsed.light?.text ?? null,
          },
        };
      }
    } catch {
      /* ignore */
    }
  }
  // Migrate the legacy single accent (applied regardless of theme) into both themes.
  const legacy = readMigrated(LEGACY_ACCENT_STORAGE_KEY, userId);
  if (legacy && /^#[0-9a-fA-F]{6}$/.test(legacy)) {
    return {
      dark: { accent: legacy.toLowerCase(), bg: null, text: null },
      light: { accent: legacy.toLowerCase(), bg: null, text: null },
    };
  }
  return DEFAULT_SETTINGS;
}

/** Convert a #rrggbb hex color to an rgba() string with the given alpha. */
function hexToRgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Lighten (percent > 0) or darken (percent < 0) a #rrggbb color by mixing it
 * toward white or black. Returns the resulting #rrggbb hex.
 */
function shade(hex: string, percent: number): string {
  const n = parseInt(hex.slice(1), 16);
  const target = percent < 0 ? 0 : 255;
  const p = Math.abs(percent);
  const mix = (c: number) => Math.round((target - c) * p + c);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

const remove = (el: HTMLElement, vars: string[]) => vars.forEach((v) => el.style.removeProperty(v));

/** Apply (or clear) the per-theme color overrides on <html>. */
function applySettings(settings: ThemeSettings, theme: ThemeMode): void {
  const el = document.documentElement;

  // Accent + derived -hover/-muted/-glow.
  if (settings.accent) {
    el.style.setProperty('--accent', settings.accent);
    el.style.setProperty(
      '--accent-hover',
      theme === 'light' ? shade(settings.accent, -0.15) : shade(settings.accent, 0.12),
    );
    el.style.setProperty(
      '--accent-muted',
      hexToRgba(settings.accent, theme === 'light' ? 0.1 : 0.15),
    );
    el.style.setProperty(
      '--accent-glow',
      hexToRgba(settings.accent, theme === 'light' ? 0.22 : 0.35),
    );
  } else {
    remove(el, ['--accent', '--accent-hover', '--accent-muted', '--accent-glow']);
  }

  // Background + derived elevated/surface/card (kept slightly layered so cards stay readable).
  if (settings.bg) {
    el.style.setProperty('--bg', settings.bg);
    el.style.setProperty(
      '--bg-elevated',
      theme === 'light' ? shade(settings.bg, -0.02) : shade(settings.bg, 0.04),
    );
    el.style.setProperty(
      '--surface',
      theme === 'light' ? shade(settings.bg, 0.03) : shade(settings.bg, 0.06),
    );
    el.style.setProperty(
      '--card',
      theme === 'light' ? shade(settings.bg, 0.03) : shade(settings.bg, 0.06),
    );
  } else {
    remove(el, ['--bg', '--bg-elevated', '--surface', '--card']);
  }

  // Text + derived -dim/-muted.
  if (settings.text) {
    el.style.setProperty('--text', settings.text);
    el.style.setProperty(
      '--text-dim',
      theme === 'light' ? shade(settings.text, 0.35) : shade(settings.text, -0.35),
    );
    el.style.setProperty(
      '--text-muted',
      theme === 'light' ? shade(settings.text, 0.55) : shade(settings.text, -0.5),
    );
  } else {
    remove(el, ['--text', '--text-dim', '--text-muted']);
  }
}

function ColorRow({
  label,
  value,
  presets,
  onSelect,
  onDefault,
}: {
  label: string;
  value: ColorValue;
  presets: ColorPreset[];
  onSelect: (v: string) => void;
  onDefault: () => void;
}) {
  return (
    <>
      <div className="theme-settings-row">
        <span className="theme-settings-label">{label}</span>
        <button className="theme-settings-default" onClick={onDefault} disabled={!value}>
          Default
        </button>
      </div>
      <div className="accent-presets">
        {presets.map((p) => (
          <button
            key={p.value}
            className={`accent-swatch${value === p.value ? ' active' : ''}`}
            style={{ background: p.value }}
            title={p.name}
            aria-label={p.name}
            onClick={() => onSelect(p.value)}
          />
        ))}
        <label className="accent-custom" title="Custom color">
          <input
            type="color"
            value={value ?? presets[0]?.value ?? '#e0a44c'}
            onChange={(e) => onSelect(e.target.value)}
          />
        </label>
      </div>
    </>
  );
}

/**
 * SINGLE writer for the light/dark mode — `<html data-theme>` + the per-user
 * storage key. Shared by this component AND by the command palette's
 * « Toggle theme » action (P2b) so the palette never re-implements the
 * `bandz-theme:<userId>` keying. It also broadcasts `bandz-theme-mode` so any
 * mounted ThemeToggle re-reads the mode instead of showing a stale sun/moon.
 */
export function applyThemeMode(mode: ThemeMode, userId: number | null): void {
  document.documentElement.setAttribute('data-theme', mode);
  try {
    localStorage.setItem(scoped(THEME_STORAGE_KEY, userId), mode);
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('bandz-theme-mode', { detail: mode }));
}

/** Flip light ↔ dark for the signed-in user (`null` = the shared `:anon` key). */
export function toggleThemeMode(userId: number | null): void {
  const isLight = document.documentElement.getAttribute('data-theme') === 'light';
  applyThemeMode(isLight ? 'dark' : 'light', userId);
}

export default function ThemeToggle() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [theme, setTheme] = useState<ThemeMode>(() => getInitialTheme(userId));
  const [settings, setSettings] = useState<SettingsByTheme>(() => getInitialSettings(userId));
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const gearRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const POPOVER_WIDTH = 236;
  const GAP = 8;

  // When the signed-in user changes, reload THAT user's own theme + settings so
  // two people on the same browser never share (or overwrite) a theme.
  useEffect(() => {
    setTheme(getInitialTheme(userId));
    setSettings(getInitialSettings(userId));
  }, [userId]);

  useEffect(() => {
    applyThemeMode(theme, userId);
  }, [theme, userId]);

  // P2b — the command palette flips the mode through the SAME writer: re-read
  // it here so this button never shows a stale sun/moon. Re-applying the value
  // we just set is a no-op (`setTheme` to the same value bails out).
  useEffect(() => {
    const onMode = (e: Event) => setTheme((e as CustomEvent<ThemeMode>).detail);
    window.addEventListener('bandz-theme-mode', onMode);
    return () => window.removeEventListener('bandz-theme-mode', onMode);
  }, []);

  useEffect(() => {
    applySettings(settings[theme], theme);
    try {
      localStorage.setItem(scoped(SETTINGS_STORAGE_KEY, userId), JSON.stringify(settings));
      // One-time migration away from the legacy single accent key.
      localStorage.removeItem(scoped(LEGACY_ACCENT_STORAGE_KEY, userId));
    } catch { /* ignore */ }
  }, [settings, theme, userId]);

  // Close the popover on outside click (but not when clicking inside the popover itself).
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (
        gearRef.current &&
        popRef.current &&
        !gearRef.current.contains(t) &&
        !popRef.current.contains(t)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function toggleOpen() {
    if (!open && gearRef.current) {
      const r = gearRef.current.getBoundingClientRect();
      // Open to the RIGHT of the gear button (it sits in the left sidebar), clamped
      // to the viewport so the popover never renders off-screen (which made the
      // color swatches unclickable).
      let left = r.right + GAP;
      if (left + POPOVER_WIDTH > window.innerWidth) {
        left = Math.max(GAP, r.left - POPOVER_WIDTH - GAP);
      }
      setPos({ top: r.bottom + 6, left });
    }
    setOpen((o) => !o);
  }

  function setSetting(mode: ThemeMode, key: keyof ThemeSettings, value: ColorValue) {
    setSettings((s) => ({ ...s, [mode]: { ...s[mode], [key]: value } }));
  }

  const active = settings[theme];

  return (
    <div className="theme-settings">
      <button
        className="logout-btn theme-toggle-btn"
        onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
        title={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
        aria-label="Toggle theme"
      >
        {theme === 'light' ? '☀️' : '🌙'}
      </button>
      <button
        ref={gearRef}
        className="logout-btn theme-toggle-btn"
        onClick={toggleOpen}
        title="Theme settings"
        aria-label="Theme settings"
        aria-expanded={open}
      >
        ⚙️
      </button>
      {open && pos && (
        <div ref={popRef} className="theme-settings-popover" style={{ top: pos.top, left: pos.left }}>
          <div className="theme-settings-heading">
            {theme === 'light' ? 'Light mode settings' : 'Dark mode settings'}
          </div>

          <div className="theme-settings-row">
            <span className="theme-settings-label">Mode</span>
            <button
              className="theme-mode-btn"
              onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
            >
              {theme === 'light' ? 'Light' : 'Dark'}
            </button>
          </div>

          <ColorRow
            label="Accent color"
            value={active.accent}
            presets={ACCENT_PRESETS}
            onSelect={(v) => setSetting(theme, 'accent', v)}
            onDefault={() => setSetting(theme, 'accent', null)}
          />
          <ColorRow
            label="Background"
            value={active.bg}
            presets={BACKGROUND_PRESETS[theme]}
            onSelect={(v) => setSetting(theme, 'bg', v)}
            onDefault={() => setSetting(theme, 'bg', null)}
          />
          <ColorRow
            label="Text color"
            value={active.text}
            presets={TEXT_PRESETS[theme]}
            onSelect={(v) => setSetting(theme, 'text', v)}
            onDefault={() => setSetting(theme, 'text', null)}
          />
        </div>
      )}
    </div>
  );
}
